/**
 * myCargoCFS Customer Portal
 * Features:
 * - Order tracking and status
 * - Active shipments list
 * - Inventory view
 * - Cargo photos gallery
 * - Truck pre-alerts with document upload
 * - Request additional services
 * - Report problems
 * - FCL tracking (Port of Koper)
 */

import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { API_URL } from "../config/api";
import {
  Warehouse,
  Package,
  Search,
  Loader2,
  X,
  ChevronRight,
  CheckCircle,
  Clock,
  Camera,
  FileText,
  AlertTriangle,
  Truck,
  Mail,
  Phone,
  ArrowLeft,
  Plus,
  Send,
  Eye,
  Download,
  ExternalLink,
  Layers,
  Shield,
  ClipboardCheck,
  Receipt,
  Image,
  Wrench,
  Upload,
  Calendar,
  MapPin,
  Ship,
  AlertCircle,
  MessageSquare,
  Box,
  Home,
  FileUp,
  Anchor,
} from "lucide-react";


// Available additional services customers can request
const ADDITIONAL_SERVICES = [
  { id: "palletisation", label: "Palletisation", description: "Consolidate loose cargo onto pallets", icon: Layers, price: "On request" },
  { id: "stretch_foiling", label: "Stretch Foiling", description: "Wrap pallets with stretch film for protection", icon: Shield, price: "On request" },
  { id: "additional_photos", label: "Additional Photos", description: "Request detailed photos of your cargo", icon: Camera, price: "Free" },
  { id: "warehouse_receipt", label: "Warehouse Receipt", description: "Official receipt document for stored goods", icon: Receipt, price: "Free" },
  { id: "dispatch_note", label: "Dispatch Note", description: "Documentation for outgoing shipments", icon: FileText, price: "Free" },
  { id: "damage_inspection", label: "Damage Inspection", description: "Detailed inspection and damage report", icon: ClipboardCheck, price: "On request" },
  { id: "damage_report", label: "Damage Report", description: "Official damage documentation with photos", icon: AlertTriangle, price: "On request" },
  { id: "labeling", label: "Labeling & Marking", description: "Custom labels or markings on cargo", icon: FileText, price: "On request" },
  { id: "repacking", label: "Repacking", description: "Repack cargo into different packaging", icon: Package, price: "On request" },
  { id: "quality_check", label: "Quality Check", description: "Verify quantity and condition of goods", icon: ClipboardCheck, price: "On request" },
];

export default function WMSCustomerPortalPage() {
  const [customerEmail, setCustomerEmail] = useState("");
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState("orders");
  
  // Data states
  const [orders, setOrders] = useState([]);
  const [inventory, setInventory] = useState(null);
  const [photos, setPhotos] = useState([]);
  const [preAlerts, setPreAlerts] = useState([]);
  const [problemReports, setProblemReports] = useState([]);
  
  // Modal states
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [showServiceRequest, setShowServiceRequest] = useState(false);
  const [showPreAlertForm, setShowPreAlertForm] = useState(false);
  const [showProblemReport, setShowProblemReport] = useState(false);
  const [showFCLTracking, setShowFCLTracking] = useState(false);

  // Login handler
  const handleLogin = async (e) => {
    e.preventDefault();
    if (!customerEmail.trim()) {
      toast.error("Please enter your email");
      return;
    }

    setLoading(true);
    try {
      const response = await axios.get(`${API_URL}/wms/customer/orders?email=${encodeURIComponent(customerEmail)}`);
      setOrders(response.data.orders || []);
      setIsLoggedIn(true);
      
      // Fetch all customer data
      await Promise.all([
        fetchInventory(),
        fetchPhotos(),
        fetchPreAlerts(),
        fetchProblemReports(),
      ]);
      
      if (response.data.orders?.length === 0) {
        toast.info("No orders found for this email");
      } else {
        toast.success(`Welcome! Found ${response.data.orders.length} order(s)`);
      }
    } catch (error) {
      toast.error("Failed to fetch orders. Please check your email.");
    } finally {
      setLoading(false);
    }
  };

  const fetchOrders = async () => {
    try {
      const response = await axios.get(`${API_URL}/wms/customer/orders?email=${encodeURIComponent(customerEmail)}`);
      setOrders(response.data.orders || []);
    } catch (error) {
      console.error("Failed to fetch orders", error);
    }
  };

  const fetchInventory = async () => {
    try {
      const response = await axios.get(`${API_URL}/wms/customer/inventory?email=${encodeURIComponent(customerEmail)}`);
      setInventory(response.data);
    } catch (error) {
      console.error("Failed to fetch inventory", error);
    }
  };

  const fetchPhotos = async () => {
    try {
      const response = await axios.get(`${API_URL}/wms/customer/photos?email=${encodeURIComponent(customerEmail)}`);
      setPhotos(response.data.photos || []);
    } catch (error) {
      console.error("Failed to fetch photos", error);
    }
  };

  const fetchPreAlerts = async () => {
    try {
      const response = await axios.get(`${API_URL}/wms/customer/pre-alerts?email=${encodeURIComponent(customerEmail)}`);
      setPreAlerts(response.data.pre_alerts || []);
    } catch (error) {
      console.error("Failed to fetch pre-alerts", error);
    }
  };

  const fetchProblemReports = async () => {
    try {
      const response = await axios.get(`${API_URL}/wms/customer/problem-reports?email=${encodeURIComponent(customerEmail)}`);
      setProblemReports(response.data.reports || []);
    } catch (error) {
      console.error("Failed to fetch problem reports", error);
    }
  };

  const handleLogout = () => {
    setIsLoggedIn(false);
    setOrders([]);
    setInventory(null);
    setPhotos([]);
    setPreAlerts([]);
    setProblemReports([]);
    setCustomerEmail("");
    setActiveTab("orders");
  };

  const tabs = [
    { id: "orders", label: "My Orders", icon: Package, count: orders.length },
    { id: "inventory", label: "Inventory", icon: Warehouse, count: inventory?.total_orders_in_warehouse || 0 },
    { id: "photos", label: "Photos", icon: Camera, count: photos.length },
    { id: "pre-alerts", label: "Truck Alerts", icon: Truck, count: preAlerts.length },
    { id: "tracking", label: "FCL Track", icon: Ship },
    { id: "problems", label: "Support", icon: AlertCircle, count: problemReports.filter(r => r.status === "open").length },
  ];

  const statusColors = {
    new: "bg-blue-100 text-blue-700",
    confirmed: "bg-purple-100 text-purple-700",
    in_progress: "bg-yellow-100 text-yellow-700",
    pending_pickup: "bg-orange-100 text-orange-700",
    completed: "bg-green-100 text-green-700",
    cancelled: "bg-red-100 text-red-700",
    in_warehouse: "bg-cyan-100 text-cyan-700",
  };

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-white border-b sticky top-0 z-40">
        <div className="max-w-6xl mx-auto px-4 py-4 flex items-center justify-between">
          <Link to="/wms" className="flex items-center gap-2">
            <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-blue-600 to-blue-700 flex items-center justify-center">
              <Warehouse className="w-6 h-6 text-white" />
            </div>
            <div>
              <span className="font-bold text-xl text-slate-900">myCargoCFS</span>
              <span className="text-xs text-slate-500 block -mt-1">Customer Portal</span>
            </div>
          </Link>
          <div className="flex items-center gap-3">
            {isLoggedIn && (
              <>
                <span className="text-sm text-slate-500 hidden sm:block">{customerEmail}</span>
                <Button variant="outline" size="sm" onClick={handleLogout}>
                  Logout
                </Button>
              </>
            )}
            <a
              href="https://cargo-cfs.com"
              target="_blank"
              rel="noopener noreferrer"
              className="text-slate-600 hover:text-blue-600 flex items-center gap-1 text-sm"
            >
              <span className="hidden sm:inline">cargo-cfs.com</span>
              <ExternalLink className="w-3 h-3" />
            </a>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 py-8">
        {!isLoggedIn ? (
          // Login View
          <LoginView
            customerEmail={customerEmail}
            setCustomerEmail={setCustomerEmail}
            onLogin={handleLogin}
            loading={loading}
            onShowFCLTracking={() => setShowFCLTracking(true)}
          />
        ) : (
          // Main Portal View
          <div className="space-y-6">
            {/* Tab Navigation */}
            <div className="bg-white rounded-xl border p-2 flex gap-1 overflow-x-auto">
              {tabs.map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors whitespace-nowrap ${
                    activeTab === tab.id
                      ? "bg-blue-600 text-white"
                      : "text-slate-600 hover:bg-slate-100"
                  }`}
                  data-testid={`tab-${tab.id}`}
                >
                  <tab.icon className="w-4 h-4" />
                  {tab.label}
                  {tab.count > 0 && (
                    <span className={`text-xs px-1.5 py-0.5 rounded-full ${
                      activeTab === tab.id ? "bg-white/20" : "bg-slate-200"
                    }`}>
                      {tab.count}
                    </span>
                  )}
                </button>
              ))}
            </div>

            {/* Tab Content */}
            {activeTab === "orders" && (
              <OrdersTab
                orders={orders}
                statusColors={statusColors}
                onSelectOrder={setSelectedOrder}
                onRequestService={(order) => {
                  setSelectedOrder(order);
                  setShowServiceRequest(true);
                }}
              />
            )}
            
            {activeTab === "inventory" && (
              <InventoryTab inventory={inventory} statusColors={statusColors} />
            )}
            
            {activeTab === "photos" && (
              <PhotosTab photos={photos} />
            )}
            
            {activeTab === "pre-alerts" && (
              <PreAlertsTab
                preAlerts={preAlerts}
                onCreateNew={() => setShowPreAlertForm(true)}
                statusColors={statusColors}
                onRefresh={fetchPreAlerts}
              />
            )}
            
            {activeTab === "tracking" && (
              <FCLTrackingTab />
            )}
            
            {activeTab === "problems" && (
              <ProblemsTab
                reports={problemReports}
                onReportNew={() => setShowProblemReport(true)}
              />
            )}
          </div>
        )}
      </main>

      {/* Modals */}
      <AnimatePresence>
        {selectedOrder && !showServiceRequest && (
          <OrderDetailModal
            order={selectedOrder}
            onClose={() => setSelectedOrder(null)}
            onRequestService={() => setShowServiceRequest(true)}
          />
        )}
      </AnimatePresence>

      <AnimatePresence>
        {showServiceRequest && selectedOrder && (
          <ServiceRequestModal
            order={selectedOrder}
            onClose={() => {
              setShowServiceRequest(false);
              setSelectedOrder(null);
            }}
            onSuccess={() => {
              setShowServiceRequest(false);
              setSelectedOrder(null);
              fetchOrders();
              toast.success("Service request submitted! Our team will contact you soon.");
            }}
          />
        )}
      </AnimatePresence>

      <AnimatePresence>
        {showPreAlertForm && (
          <PreAlertFormModal
            customerEmail={customerEmail}
            onClose={() => setShowPreAlertForm(false)}
            onSuccess={() => {
              setShowPreAlertForm(false);
              fetchPreAlerts();
              toast.success("Pre-alert created! Our team has been notified.");
            }}
          />
        )}
      </AnimatePresence>

      <AnimatePresence>
        {showProblemReport && (
          <ProblemReportModal
            customerEmail={customerEmail}
            orders={orders}
            onClose={() => setShowProblemReport(false)}
            onSuccess={() => {
              setShowProblemReport(false);
              fetchProblemReports();
            }}
          />
        )}
      </AnimatePresence>

      <AnimatePresence>
        {showFCLTracking && !isLoggedIn && (
          <FCLTrackingModal onClose={() => setShowFCLTracking(false)} />
        )}
      </AnimatePresence>
    </div>
  );
}

// ==========================================
// LOGIN VIEW
// ==========================================

function LoginView({ customerEmail, setCustomerEmail, onLogin, loading, onShowFCLTracking }) {
  const [searchQuery, setSearchQuery] = useState("");
  const [searchType, setSearchType] = useState("order");

  return (
    <div className="max-w-lg mx-auto space-y-6">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-white rounded-xl border p-8"
      >
        <h1 className="text-2xl font-bold text-center mb-2">Customer Portal</h1>
        <p className="text-slate-500 text-center mb-8">
          Enter your email to access your orders, inventory, and photos
        </p>

        <form onSubmit={onLogin} className="space-y-4">
          <div>
            <label className="text-sm font-medium text-slate-700 mb-2 block">Your Email Address</label>
            <div className="flex gap-2">
              <Input
                type="email"
                value={customerEmail}
                onChange={(e) => setCustomerEmail(e.target.value)}
                placeholder="your@company.com"
                className="flex-1"
                data-testid="customer-email-input"
              />
              <Button type="submit" disabled={loading} data-testid="login-btn">
                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Login"}
              </Button>
            </div>
          </div>
        </form>

        <div className="relative my-6">
          <div className="absolute inset-0 flex items-center">
            <div className="w-full border-t" />
          </div>
          <div className="relative flex justify-center">
            <span className="bg-white px-4 text-sm text-slate-500">Quick Actions</span>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <Button
            variant="outline"
            className="flex items-center justify-center gap-2 h-auto py-4"
            onClick={onShowFCLTracking}
            data-testid="fcl-tracking-btn"
          >
            <Ship className="w-5 h-5 text-blue-600" />
            <div className="text-left">
              <p className="font-medium">FCL Tracking</p>
              <p className="text-xs text-slate-500">Port of Koper</p>
            </div>
          </Button>
          <a href="https://cargo-cfs.com" target="_blank" rel="noopener noreferrer">
            <Button variant="outline" className="w-full flex items-center justify-center gap-2 h-auto py-4">
              <ExternalLink className="w-5 h-5 text-blue-600" />
              <div className="text-left">
                <p className="font-medium">Website</p>
                <p className="text-xs text-slate-500">cargo-cfs.com</p>
              </div>
            </Button>
          </a>
        </div>
      </motion.div>
    </div>
  );
}

// ==========================================
// ORDERS TAB
// ==========================================

function OrdersTab({ orders, statusColors, onSelectOrder, onRequestService }) {
  const activeOrders = orders.filter(o => !["completed", "cancelled"].includes(o.status));
  const completedOrders = orders.filter(o => ["completed", "cancelled"].includes(o.status));

  return (
    <div className="space-y-6">
      {/* Active Orders */}
      <div>
        <h3 className="text-lg font-semibold mb-3 flex items-center gap-2">
          <Clock className="w-5 h-5 text-blue-600" />
          Active Orders ({activeOrders.length})
        </h3>
        {activeOrders.length > 0 ? (
          <div className="grid gap-4">
            {activeOrders.map((order) => (
              <OrderCard
                key={order.id}
                order={order}
                statusColors={statusColors}
                onSelect={() => onSelectOrder(order)}
                onRequestService={() => onRequestService(order)}
              />
            ))}
          </div>
        ) : (
          <div className="bg-white rounded-xl border p-8 text-center">
            <Package className="w-10 h-10 mx-auto mb-2 text-slate-300" />
            <p className="text-slate-500">No active orders</p>
          </div>
        )}
      </div>

      {/* Completed Orders */}
      {completedOrders.length > 0 && (
        <div>
          <h3 className="text-lg font-semibold mb-3 flex items-center gap-2">
            <CheckCircle className="w-5 h-5 text-green-600" />
            Completed Orders ({completedOrders.length})
          </h3>
          <div className="grid gap-4">
            {completedOrders.slice(0, 5).map((order) => (
              <OrderCard
                key={order.id}
                order={order}
                statusColors={statusColors}
                onSelect={() => onSelectOrder(order)}
                compact
              />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function OrderCard({ order, statusColors, onSelect, onRequestService, compact = false }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      className={`bg-white rounded-xl border p-4 hover:shadow-md transition-shadow ${compact ? "opacity-75" : ""}`}
    >
      <div className="flex items-start justify-between">
        <div className="flex-1 cursor-pointer" onClick={onSelect}>
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-bold text-blue-600">{order.order_number}</span>
            <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${statusColors[order.status] || statusColors.new}`}>
              {order.status?.replace("_", " ")}
            </span>
          </div>
          <p className="text-sm text-slate-500 mt-1">{order.cargo_description || "No description"}</p>
          <div className="flex flex-wrap gap-4 mt-2 text-sm text-slate-400">
            {order.bl_numbers?.length > 0 && <span>BL: {order.bl_numbers.join(", ")}</span>}
            {order.container_numbers?.length > 0 && <span>Container: {order.container_numbers.join(", ")}</span>}
            {order.packages && <span>{order.packages} pkgs</span>}
          </div>
          {/* Task Progress */}
          {order.tasks?.length > 0 && (
            <div className="mt-3 flex items-center gap-2">
              <div className="flex-1 h-1.5 bg-slate-100 rounded-full overflow-hidden max-w-xs">
                <div
                  className="h-full bg-green-500 rounded-full"
                  style={{ width: `${(order.tasks.filter((t) => t.completed).length / order.tasks.length) * 100}%` }}
                />
              </div>
              <span className="text-xs text-slate-500">
                {order.tasks.filter((t) => t.completed).length}/{order.tasks.length} tasks
              </span>
            </div>
          )}
        </div>
        <div className="flex items-center gap-2">
          {!compact && onRequestService && (
            <Button variant="outline" size="sm" onClick={onRequestService} data-testid={`request-service-${order.id}`}>
              <Plus className="w-3 h-3 mr-1" />
              Service
            </Button>
          )}
          <ChevronRight className="w-5 h-5 text-slate-400 cursor-pointer" onClick={onSelect} />
        </div>
      </div>
    </motion.div>
  );
}

// ==========================================
// INVENTORY TAB
// ==========================================

function InventoryTab({ inventory, statusColors }) {
  if (!inventory) {
    return (
      <div className="bg-white rounded-xl border p-12 text-center">
        <Loader2 className="w-8 h-8 mx-auto mb-2 animate-spin text-blue-600" />
        <p className="text-slate-500">Loading inventory...</p>
      </div>
    );
  }

  const { in_warehouse_orders, inventory: items } = inventory;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold flex items-center gap-2">
          <Warehouse className="w-5 h-5 text-blue-600" />
          Your Inventory in Warehouse
        </h3>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="bg-white rounded-xl border p-4">
          <Package className="w-5 h-5 text-blue-600 mb-2" />
          <p className="text-2xl font-bold">{in_warehouse_orders?.length || 0}</p>
          <p className="text-xs text-slate-500">Orders in Warehouse</p>
        </div>
        <div className="bg-white rounded-xl border p-4">
          <Box className="w-5 h-5 text-green-600 mb-2" />
          <p className="text-2xl font-bold">{items?.length || 0}</p>
          <p className="text-xs text-slate-500">Inventory Items</p>
        </div>
      </div>

      {/* Orders in Warehouse */}
      {in_warehouse_orders?.length > 0 ? (
        <div className="bg-white rounded-xl border overflow-hidden">
          <div className="p-4 border-b bg-slate-50">
            <h4 className="font-semibold">Orders Currently in Warehouse</h4>
          </div>
          <div className="divide-y">
            {in_warehouse_orders.map((order) => (
              <div key={order.id} className="p-4 hover:bg-slate-50">
                <div className="flex items-center justify-between">
                  <div>
                    <span className="font-bold text-blue-600">{order.order_number}</span>
                    <span className={`ml-2 px-2 py-0.5 rounded-full text-xs font-medium ${statusColors[order.status] || statusColors.new}`}>
                      {order.status?.replace("_", " ")}
                    </span>
                    <p className="text-sm text-slate-500 mt-1">{order.cargo_description || order.customer_name}</p>
                    <div className="flex gap-4 mt-1 text-xs text-slate-400">
                      {order.packages && <span>{order.packages} packages</span>}
                      {order.gross_weight_kg && <span>{order.gross_weight_kg} kg</span>}
                      {order.volume_cbm && <span>{order.volume_cbm} CBM</span>}
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="text-xs text-slate-400">Since</p>
                    <p className="text-sm font-medium">{new Date(order.created_at).toLocaleDateString()}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      ) : (
        <div className="bg-white rounded-xl border p-12 text-center">
          <Warehouse className="w-12 h-12 mx-auto mb-3 text-slate-300" />
          <p className="text-slate-500">No cargo currently in warehouse</p>
        </div>
      )}
    </div>
  );
}

// ==========================================
// PHOTOS TAB
// ==========================================

function PhotosTab({ photos }) {
  const [selectedPhoto, setSelectedPhoto] = useState(null);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold flex items-center gap-2">
          <Camera className="w-5 h-5 text-blue-600" />
          Cargo Photos ({photos.length})
        </h3>
      </div>

      {photos.length > 0 ? (
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
          {photos.map((photo, idx) => (
            <motion.div
              key={photo.id || idx}
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: idx * 0.05 }}
              className="aspect-square rounded-lg overflow-hidden border bg-white cursor-pointer hover:shadow-md transition-shadow"
              onClick={() => setSelectedPhoto(photo)}
            >
              <img
                src={photo.photo_url?.startsWith("http") ? photo.photo_url : `${API_URL}${photo.photo_url}`}
                alt={photo.description || `Photo ${idx + 1}`}
                className="w-full h-full object-cover"
                onError={(e) => { e.target.src = "/placeholder-image.png"; }}
              />
              {photo.order_number && (
                <div className="absolute bottom-0 left-0 right-0 bg-black/50 text-white text-xs p-1 truncate">
                  {photo.order_number}
                </div>
              )}
            </motion.div>
          ))}
        </div>
      ) : (
        <div className="bg-white rounded-xl border p-12 text-center">
          <Camera className="w-12 h-12 mx-auto mb-3 text-slate-300" />
          <p className="text-slate-500">No photos available yet</p>
          <p className="text-xs text-slate-400 mt-1">Photos will appear here after cargo handling</p>
        </div>
      )}

      {/* Photo Modal */}
      <AnimatePresence>
        {selectedPhoto && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4"
            onClick={() => setSelectedPhoto(null)}
          >
            <motion.img
              initial={{ scale: 0.9 }}
              animate={{ scale: 1 }}
              exit={{ scale: 0.9 }}
              src={selectedPhoto.photo_url?.startsWith("http") ? selectedPhoto.photo_url : `${API_URL}${selectedPhoto.photo_url}`}
              alt="Cargo photo"
              className="max-w-full max-h-[90vh] object-contain rounded-lg"
            />
            <button
              className="absolute top-4 right-4 text-white bg-black/50 rounded-full p-2"
              onClick={() => setSelectedPhoto(null)}
            >
              <X className="w-6 h-6" />
            </button>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

// ==========================================
// PRE-ALERTS TAB
// ==========================================

function PreAlertsTab({ preAlerts, onCreateNew, statusColors, onRefresh }) {
  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold flex items-center gap-2">
          <Truck className="w-5 h-5 text-blue-600" />
          My Truck Pre-Alerts
        </h3>
        <Button onClick={onCreateNew} data-testid="new-pre-alert-btn">
          <Plus className="w-4 h-4 mr-2" />
          Schedule Truck
        </Button>
      </div>

      {preAlerts.length > 0 ? (
        <div className="bg-white rounded-xl border overflow-hidden">
          <div className="divide-y">
            {preAlerts.map((pa) => (
              <div key={pa.id} className="p-4 hover:bg-slate-50">
                <div className="flex items-start justify-between">
                  <div>
                    <div className="flex items-center gap-2">
                      <Truck className="w-4 h-4 text-blue-600" />
                      <span className="font-medium">{pa.carrier_name}</span>
                      <span className={`px-2 py-0.5 rounded text-xs ${
                        pa.direction === "inbound" ? "bg-green-100 text-green-700" : "bg-orange-100 text-orange-700"
                      }`}>
                        {pa.direction}
                      </span>
                    </div>
                    <p className="text-sm text-slate-500 mt-1">
                      <Calendar className="w-3 h-3 inline mr-1" />
                      {pa.scheduled_date} @ {pa.scheduled_time}
                    </p>
                    {pa.truck_plate && (
                      <p className="text-xs text-slate-400 mt-1">Truck: {pa.truck_plate}</p>
                    )}
                    {pa.documents?.length > 0 && (
                      <div className="flex gap-2 mt-2">
                        {pa.documents.map((doc, i) => (
                          <a
                            key={i}
                            href={`${API_URL}${doc.url}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-xs text-blue-600 hover:underline flex items-center gap-1"
                          >
                            <FileText className="w-3 h-3" />
                            {doc.filename}
                          </a>
                        ))}
                      </div>
                    )}
                  </div>
                  <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                    pa.status === "arrived" ? "bg-green-100 text-green-700" :
                    pa.status === "scheduled" ? "bg-blue-100 text-blue-700" :
                    "bg-slate-100 text-slate-700"
                  }`}>
                    {pa.status}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
      ) : (
        <div className="bg-white rounded-xl border p-12 text-center">
          <Truck className="w-12 h-12 mx-auto mb-3 text-slate-300" />
          <p className="text-slate-500">No truck pre-alerts yet</p>
          <Button className="mt-4" onClick={onCreateNew}>
            <Plus className="w-4 h-4 mr-2" />
            Schedule Your First Truck
          </Button>
        </div>
      )}
    </div>
  );
}

// ==========================================
// FCL TRACKING TAB
// ==========================================

function FCLTrackingTab() {
  const [containerNumber, setContainerNumber] = useState("");
  const [loading, setLoading] = useState(false);
  const [trackingResult, setTrackingResult] = useState(null);

  const handleTrack = async () => {
    if (!containerNumber.trim()) {
      toast.error("Please enter a container number");
      return;
    }

    setLoading(true);
    try {
      const response = await axios.get(`${API_URL}/wms/customer/fcl-tracking?container_number=${encodeURIComponent(containerNumber)}`);
      setTrackingResult(response.data);
    } catch (error) {
      toast.error("Failed to track container");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="bg-white rounded-xl border p-6">
        <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
          <Ship className="w-5 h-5 text-blue-600" />
          FCL Container Tracking - Port of Koper
        </h3>
        
        <div className="flex gap-3">
          <div className="relative flex-1">
            <Anchor className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input
              value={containerNumber}
              onChange={(e) => setContainerNumber(e.target.value.toUpperCase())}
              onKeyDown={(e) => e.key === "Enter" && handleTrack()}
              placeholder="Enter container number (e.g., MSCU1234567)"
              className="pl-10"
              data-testid="fcl-container-input"
            />
          </div>
          <Button onClick={handleTrack} disabled={loading} data-testid="fcl-track-btn">
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
            <span className="ml-2">Track</span>
          </Button>
        </div>
      </div>

      {trackingResult && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white rounded-xl border overflow-hidden"
        >
          <div className="p-4 border-b bg-gradient-to-r from-blue-50 to-blue-100">
            <div className="flex items-center justify-between">
              <div>
                <h4 className="font-bold text-lg">{trackingResult.container_number}</h4>
                <p className="text-sm text-slate-600">{trackingResult.port}</p>
              </div>
              <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                trackingResult.found_in_system ? "bg-green-100 text-green-700" : "bg-yellow-100 text-yellow-700"
              }`}>
                {trackingResult.found_in_system ? trackingResult.status?.replace("_", " ") : "Not in system"}
              </span>
            </div>
          </div>

          <div className="p-6">
            {trackingResult.found_in_system ? (
              <div className="space-y-4">
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                  {trackingResult.order_number && (
                    <div>
                      <span className="text-slate-500">Order</span>
                      <p className="font-medium text-blue-600">{trackingResult.order_number}</p>
                    </div>
                  )}
                  {trackingResult.vessel_name && (
                    <div>
                      <span className="text-slate-500">Vessel</span>
                      <p className="font-medium">{trackingResult.vessel_name}</p>
                    </div>
                  )}
                  {trackingResult.eta && (
                    <div>
                      <span className="text-slate-500">ETA</span>
                      <p className="font-medium">{new Date(trackingResult.eta).toLocaleDateString()}</p>
                    </div>
                  )}
                  {trackingResult.etd && (
                    <div>
                      <span className="text-slate-500">ETD</span>
                      <p className="font-medium">{new Date(trackingResult.etd).toLocaleDateString()}</p>
                    </div>
                  )}
                </div>

                {trackingResult.events?.length > 0 && (
                  <div>
                    <h5 className="font-semibold mb-3">Timeline</h5>
                    <div className="space-y-3">
                      {trackingResult.events.map((event, idx) => (
                        <div key={idx} className="flex gap-3">
                          <div className="w-2 h-2 mt-2 rounded-full bg-blue-500" />
                          <div>
                            <p className="font-medium">{event.status}</p>
                            <p className="text-sm text-slate-500">{event.details}</p>
                            {event.timestamp && (
                              <p className="text-xs text-slate-400">{new Date(event.timestamp).toLocaleString()}</p>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {trackingResult.pre_alert && (
                  <div className="bg-blue-50 rounded-lg p-4">
                    <h5 className="font-semibold mb-2 flex items-center gap-2">
                      <Truck className="w-4 h-4" />
                      Scheduled Pickup
                    </h5>
                    <p className="text-sm">
                      {trackingResult.pre_alert.scheduled_date} @ {trackingResult.pre_alert.scheduled_time}
                    </p>
                    <p className="text-xs text-slate-500">Carrier: {trackingResult.pre_alert.carrier}</p>
                  </div>
                )}
              </div>
            ) : (
              <div className="text-center py-8">
                <Ship className="w-12 h-12 mx-auto mb-3 text-slate-300" />
                <p className="text-slate-500">Container not found in our system</p>
                <p className="text-sm text-slate-400 mt-1">
                  Please contact us for more information about this container
                </p>
              </div>
            )}
          </div>
        </motion.div>
      )}
    </div>
  );
}

// ==========================================
// PROBLEMS TAB
// ==========================================

function ProblemsTab({ reports, onReportNew }) {
  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold flex items-center gap-2">
          <AlertCircle className="w-5 h-5 text-blue-600" />
          Support & Problem Reports
        </h3>
        <Button onClick={onReportNew} variant="destructive" data-testid="report-problem-btn">
          <AlertTriangle className="w-4 h-4 mr-2" />
          Report Problem
        </Button>
      </div>

      {reports.length > 0 ? (
        <div className="bg-white rounded-xl border overflow-hidden">
          <div className="divide-y">
            {reports.map((report) => (
              <div key={report.id} className="p-4 hover:bg-slate-50">
                <div className="flex items-start justify-between">
                  <div>
                    <div className="flex items-center gap-2">
                      <span className={`w-2 h-2 rounded-full ${
                        report.severity === "high" ? "bg-red-500" :
                        report.severity === "medium" ? "bg-orange-500" : "bg-yellow-500"
                      }`} />
                      <span className="font-medium">{report.subject}</span>
                    </div>
                    <p className="text-sm text-slate-500 mt-1">{report.problem_label}</p>
                    <p className="text-xs text-slate-400 mt-1">
                      Reported: {new Date(report.created_at).toLocaleDateString()}
                    </p>
                  </div>
                  <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                    report.status === "open" ? "bg-orange-100 text-orange-700" :
                    report.status === "resolved" ? "bg-green-100 text-green-700" :
                    "bg-slate-100 text-slate-700"
                  }`}>
                    {report.status}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
      ) : (
        <div className="bg-white rounded-xl border p-12 text-center">
          <MessageSquare className="w-12 h-12 mx-auto mb-3 text-slate-300" />
          <p className="text-slate-500">No problem reports</p>
          <p className="text-sm text-slate-400 mt-1">Need help? Click the button above to report an issue.</p>
        </div>
      )}

      {/* Contact Info */}
      <div className="bg-blue-50 rounded-xl border border-blue-200 p-6">
        <h4 className="font-semibold mb-3 flex items-center gap-2">
          <Phone className="w-5 h-5 text-blue-600" />
          Contact Support
        </h4>
        <div className="grid md:grid-cols-3 gap-4 text-sm">
          <div className="flex items-center gap-2">
            <Mail className="w-4 h-4 text-slate-500" />
            <a href="mailto:consulting@cargo-cfs.com" className="text-blue-600 hover:underline">
              consulting@cargo-cfs.com
            </a>
          </div>
          <div className="flex items-center gap-2">
            <Phone className="w-4 h-4 text-slate-500" />
            <span>+386 5 XXX XXXX</span>
          </div>
          <div className="flex items-center gap-2">
            <MapPin className="w-4 h-4 text-slate-500" />
            <span>Luka Koper, Slovenia</span>
          </div>
        </div>
      </div>
    </div>
  );
}

// ==========================================
// MODALS
// ==========================================

// Order Detail Modal
function OrderDetailModal({ order, onClose, onRequestService }) {
  const statusColors = {
    new: "bg-blue-100 text-blue-700 border-blue-200",
    confirmed: "bg-purple-100 text-purple-700 border-purple-200",
    in_progress: "bg-yellow-100 text-yellow-700 border-yellow-200",
    pending_pickup: "bg-orange-100 text-orange-700 border-orange-200",
    completed: "bg-green-100 text-green-700 border-green-200",
    cancelled: "bg-red-100 text-red-700 border-red-200",
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
      onClick={onClose}
    >
      <motion.div
        initial={{ scale: 0.9, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.9, opacity: 0 }}
        className="bg-white rounded-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="p-6 border-b sticky top-0 bg-white">
          <div className="flex items-center justify-between">
            <div>
              <div className="flex items-center gap-2">
                <h3 className="text-lg font-bold text-blue-600">{order.order_number}</h3>
                <span className={`px-2 py-0.5 rounded-full text-xs font-medium border ${statusColors[order.status] || statusColors.new}`}>
                  {order.status?.replace("_", " ")}
                </span>
              </div>
              <p className="text-slate-500 mt-1">{order.customer_name}</p>
            </div>
            <Button variant="ghost" size="sm" onClick={onClose}>
              <X className="w-4 h-4" />
            </Button>
          </div>
        </div>

        {/* Order Info */}
        <div className="p-6 border-b">
          <div className="grid grid-cols-2 gap-4 text-sm">
            {order.reference_number && (
              <div><span className="text-slate-500">Reference</span><p className="font-medium">{order.reference_number}</p></div>
            )}
            {order.bl_numbers?.length > 0 && (
              <div><span className="text-slate-500">BL Numbers</span><p className="font-medium">{order.bl_numbers.join(", ")}</p></div>
            )}
            {order.container_numbers?.length > 0 && (
              <div><span className="text-slate-500">Containers</span><p className="font-medium">{order.container_numbers.join(", ")}</p></div>
            )}
            {order.packages && (
              <div><span className="text-slate-500">Packages</span><p className="font-medium">{order.packages}</p></div>
            )}
            {order.gross_weight_kg && (
              <div><span className="text-slate-500">Weight</span><p className="font-medium">{order.gross_weight_kg} kg</p></div>
            )}
            {order.volume_cbm && (
              <div><span className="text-slate-500">Volume</span><p className="font-medium">{order.volume_cbm} CBM</p></div>
            )}
          </div>
          {order.cargo_description && (
            <div className="mt-4">
              <span className="text-slate-500 text-sm">Cargo Description</span>
              <p className="font-medium">{order.cargo_description}</p>
            </div>
          )}
        </div>

        {/* Photos */}
        {order.photos?.length > 0 && (
          <div className="p-6 border-b">
            <h4 className="font-semibold mb-3 flex items-center gap-2">
              <Camera className="w-5 h-5 text-blue-600" />
              Cargo Photos
            </h4>
            <div className="grid grid-cols-4 gap-2">
              {order.photos.map((photo, idx) => (
                <img
                  key={idx}
                  src={photo.photo_url?.startsWith("http") ? photo.photo_url : `${API_URL}${photo.photo_url}`}
                  alt={`Photo ${idx + 1}`}
                  className="w-full h-20 object-cover rounded-lg"
                />
              ))}
            </div>
          </div>
        )}

        {/* Progress Timeline */}
        {order.tasks?.length > 0 && (
          <div className="p-6 border-b">
            <h4 className="font-semibold mb-4 flex items-center gap-2">
              <Clock className="w-5 h-5 text-blue-600" />
              Progress
            </h4>
            <div className="space-y-3">
              {order.tasks.map((task, idx) => (
                <div key={task.id || idx} className="flex items-center gap-3">
                  <div className={`w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 ${
                    task.completed ? "bg-green-100 text-green-600" : "bg-slate-100 text-slate-400"
                  }`}>
                    {task.completed ? <CheckCircle className="w-4 h-4" /> : <span className="w-2 h-2 rounded-full bg-current" />}
                  </div>
                  <div className="flex-1">
                    <p className={`font-medium ${task.completed ? "text-slate-900" : "text-slate-500"}`}>{task.label}</p>
                    {task.completed_at && <p className="text-xs text-slate-400">{new Date(task.completed_at).toLocaleString()}</p>}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Service Requests */}
        {order.service_requests?.length > 0 && (
          <div className="p-6 border-b">
            <h4 className="font-semibold mb-4 flex items-center gap-2">
              <Wrench className="w-5 h-5 text-blue-600" />
              Service Requests
            </h4>
            <div className="space-y-2">
              {order.service_requests.map((req, idx) => (
                <div key={idx} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                  <div>
                    <p className="font-medium">{req.service_label}</p>
                    <p className="text-sm text-slate-500">Requested: {new Date(req.requested_at).toLocaleDateString()}</p>
                  </div>
                  <span className={`px-2 py-1 rounded text-xs font-medium ${
                    req.status === "completed" ? "bg-green-100 text-green-700" :
                    req.status === "in_progress" ? "bg-yellow-100 text-yellow-700" : "bg-blue-100 text-blue-700"
                  }`}>
                    {req.status}
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Actions */}
        <div className="p-6 bg-slate-50 flex gap-3">
          <Button onClick={onRequestService} className="flex-1" data-testid="modal-request-service-btn">
            <Plus className="w-4 h-4 mr-2" />
            Request Additional Service
          </Button>
          <Button variant="outline">
            <Download className="w-4 h-4 mr-2" />
            Download Receipt
          </Button>
        </div>
      </motion.div>
    </motion.div>
  );
}

// Service Request Modal
function ServiceRequestModal({ order, onClose, onSuccess }) {
  const [selectedServices, setSelectedServices] = useState([]);
  const [notes, setNotes] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const toggleService = (serviceId) => {
    setSelectedServices((prev) =>
      prev.includes(serviceId) ? prev.filter((id) => id !== serviceId) : [...prev, serviceId]
    );
  };

  const handleSubmit = async () => {
    if (selectedServices.length === 0) {
      toast.error("Please select at least one service");
      return;
    }

    setSubmitting(true);
    try {
      await axios.post(`${API_URL}/wms/customer/service-request`, {
        order_id: order.id,
        services: selectedServices,
        notes: notes,
      });
      onSuccess();
    } catch (error) {
      toast.error("Failed to submit request");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
      onClick={onClose}
    >
      <motion.div
        initial={{ scale: 0.9, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.9, opacity: 0 }}
        className="bg-white rounded-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="p-6 border-b sticky top-0 bg-white">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-lg font-bold">Request Additional Services</h3>
              <p className="text-slate-500 text-sm">Order: {order.order_number}</p>
            </div>
            <Button variant="ghost" size="sm" onClick={onClose}>
              <X className="w-4 h-4" />
            </Button>
          </div>
        </div>

        <div className="p-6">
          <p className="text-sm text-slate-600 mb-4">
            Select the services you need. Our team will process your request and update you.
          </p>

          <div className="grid gap-3">
            {ADDITIONAL_SERVICES.map((service) => {
              const isSelected = selectedServices.includes(service.id);
              return (
                <button
                  key={service.id}
                  onClick={() => toggleService(service.id)}
                  className={`flex items-center gap-4 p-4 rounded-lg border text-left transition-colors ${
                    isSelected ? "border-blue-500 bg-blue-50" : "border-slate-200 hover:border-slate-300"
                  }`}
                  data-testid={`service-${service.id}`}
                >
                  <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                    isSelected ? "bg-blue-500 text-white" : "bg-slate-100 text-slate-600"
                  }`}>
                    <service.icon className="w-5 h-5" />
                  </div>
                  <div className="flex-1">
                    <p className="font-medium">{service.label}</p>
                    <p className="text-sm text-slate-500">{service.description}</p>
                  </div>
                  <div className="text-right">
                    <span className="text-xs text-slate-400">{service.price}</span>
                    {isSelected && <CheckCircle className="w-5 h-5 text-blue-500 mt-1" />}
                  </div>
                </button>
              );
            })}
          </div>

          <div className="mt-6">
            <label className="text-sm font-medium mb-2 block">Additional Notes</label>
            <Textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Any special instructions or details..."
              rows={3}
            />
          </div>
        </div>

        <div className="p-6 border-t bg-slate-50 flex gap-3">
          <Button variant="outline" onClick={onClose} className="flex-1">Cancel</Button>
          <Button onClick={handleSubmit} disabled={submitting || selectedServices.length === 0} className="flex-1" data-testid="submit-service-request-btn">
            {submitting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Send className="w-4 h-4 mr-2" />}
            Submit Request ({selectedServices.length})
          </Button>
        </div>
      </motion.div>
    </motion.div>
  );
}

// Pre-Alert Form Modal
function PreAlertFormModal({ customerEmail, onClose, onSuccess }) {
  const [formData, setFormData] = useState({
    carrier_name: "",
    driver_name: "",
    driver_phone: "",
    truck_plate: "",
    scheduled_date: new Date().toISOString().split("T")[0],
    scheduled_time: "09:00",
    direction: "inbound",
    cargo_description: "",
    expected_pallets: "",
    reference_number: "",
    notes: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [documents, setDocuments] = useState([]);
  const [uploading, setUploading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.carrier_name) {
      toast.error("Carrier name is required");
      return;
    }

    setSubmitting(true);
    try {
      const response = await axios.post(`${API_URL}/wms/customer/pre-alerts`, {
        ...formData,
        customer_email: customerEmail,
        expected_pallets: formData.expected_pallets ? parseInt(formData.expected_pallets) : null,
      });

      // Upload documents if any
      if (documents.length > 0 && response.data.pre_alert?.id) {
        for (const doc of documents) {
          const formPayload = new FormData();
          formPayload.append("file", doc);
          formPayload.append("document_type", "general");
          formPayload.append("customer_email", customerEmail);
          
          await axios.post(`${API_URL}/wms/customer/pre-alerts/${response.data.pre_alert.id}/documents`, formPayload);
        }
      }

      onSuccess();
    } catch (error) {
      toast.error("Failed to create pre-alert");
    } finally {
      setSubmitting(false);
    }
  };

  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files || []);
    setDocuments((prev) => [...prev, ...files]);
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
      onClick={onClose}
    >
      <motion.div
        initial={{ scale: 0.9, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.9, opacity: 0 }}
        className="bg-white rounded-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="p-6 border-b">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-bold">Schedule Truck Arrival</h3>
            <Button variant="ghost" size="sm" onClick={onClose}>
              <X className="w-4 h-4" />
            </Button>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-sm font-medium">Carrier Name *</label>
              <Input
                value={formData.carrier_name}
                onChange={(e) => setFormData({ ...formData, carrier_name: e.target.value })}
                placeholder="Transport Company"
                required
              />
            </div>
            <div>
              <label className="text-sm font-medium">Direction</label>
              <select
                value={formData.direction}
                onChange={(e) => setFormData({ ...formData, direction: e.target.value })}
                className="w-full h-10 border rounded-md px-3"
              >
                <option value="inbound">Inbound (Delivery)</option>
                <option value="outbound">Outbound (Pickup)</option>
              </select>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-sm font-medium">Scheduled Date *</label>
              <Input
                type="date"
                value={formData.scheduled_date}
                onChange={(e) => setFormData({ ...formData, scheduled_date: e.target.value })}
                required
              />
            </div>
            <div>
              <label className="text-sm font-medium">Scheduled Time *</label>
              <Input
                type="time"
                value={formData.scheduled_time}
                onChange={(e) => setFormData({ ...formData, scheduled_time: e.target.value })}
                required
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-sm font-medium">Driver Name</label>
              <Input
                value={formData.driver_name}
                onChange={(e) => setFormData({ ...formData, driver_name: e.target.value })}
                placeholder="John Doe"
              />
            </div>
            <div>
              <label className="text-sm font-medium">Driver Phone</label>
              <Input
                value={formData.driver_phone}
                onChange={(e) => setFormData({ ...formData, driver_phone: e.target.value })}
                placeholder="+386..."
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-sm font-medium">Truck Plate</label>
              <Input
                value={formData.truck_plate}
                onChange={(e) => setFormData({ ...formData, truck_plate: e.target.value })}
                placeholder="LJ-123-AB"
              />
            </div>
            <div>
              <label className="text-sm font-medium">Reference Number</label>
              <Input
                value={formData.reference_number}
                onChange={(e) => setFormData({ ...formData, reference_number: e.target.value })}
                placeholder="PO-12345"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-sm font-medium">Expected Pallets</label>
              <Input
                type="number"
                value={formData.expected_pallets}
                onChange={(e) => setFormData({ ...formData, expected_pallets: e.target.value })}
                placeholder="33"
              />
            </div>
          </div>

          <div>
            <label className="text-sm font-medium">Cargo Description</label>
            <Textarea
              value={formData.cargo_description}
              onChange={(e) => setFormData({ ...formData, cargo_description: e.target.value })}
              placeholder="Electronics, machinery parts, etc."
              rows={2}
            />
          </div>

          {/* Document Upload */}
          <div>
            <label className="text-sm font-medium">Attach Documents</label>
            <div className="border-2 border-dashed rounded-lg p-4 text-center">
              <label className="cursor-pointer">
                <input
                  type="file"
                  multiple
                  onChange={handleFileSelect}
                  className="hidden"
                  accept=".pdf,.doc,.docx,.xls,.xlsx,.jpg,.jpeg,.png"
                />
                <Upload className="w-6 h-6 mx-auto mb-2 text-slate-400" />
                <p className="text-sm text-slate-500">Click to upload documents</p>
                <p className="text-xs text-slate-400">PDF, DOC, XLS, Images</p>
              </label>
            </div>
            {documents.length > 0 && (
              <div className="mt-2 space-y-1">
                {documents.map((doc, idx) => (
                  <div key={idx} className="flex items-center gap-2 text-sm text-slate-600">
                    <FileText className="w-4 h-4" />
                    <span>{doc.name}</span>
                    <button
                      type="button"
                      onClick={() => setDocuments((prev) => prev.filter((_, i) => i !== idx))}
                      className="text-red-500 ml-auto"
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div>
            <label className="text-sm font-medium">Notes</label>
            <Textarea
              value={formData.notes}
              onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
              placeholder="Special instructions..."
              rows={2}
            />
          </div>

          <div className="flex gap-3 pt-4">
            <Button type="button" variant="outline" onClick={onClose} className="flex-1">Cancel</Button>
            <Button type="submit" disabled={submitting} className="flex-1" data-testid="submit-pre-alert-btn">
              {submitting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Truck className="w-4 h-4 mr-2" />}
              Schedule Truck
            </Button>
          </div>
        </form>
      </motion.div>
    </motion.div>
  );
}

// Problem Report Modal
function ProblemReportModal({ customerEmail, orders, onClose, onSuccess }) {
  const [problemTypes, setProblemTypes] = useState([]);
  const [formData, setFormData] = useState({
    problem_type: "",
    order_id: "",
    subject: "",
    description: "",
    contact_phone: "",
  });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    fetchProblemTypes();
  }, []);

  const fetchProblemTypes = async () => {
    try {
      const response = await axios.get(`${API_URL}/wms/customer/problem-types`);
      setProblemTypes(response.data.problem_types || []);
    } catch (error) {
      console.error("Failed to fetch problem types", error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.problem_type || !formData.subject || !formData.description) {
      toast.error("Please fill in all required fields");
      return;
    }

    setSubmitting(true);
    try {
      await axios.post(`${API_URL}/wms/customer/report-problem`, {
        ...formData,
        customer_email: customerEmail,
      });
      toast.success("Problem reported! Our team will contact you soon.");
      onSuccess();
    } catch (error) {
      toast.error("Failed to submit report");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
      onClick={onClose}
    >
      <motion.div
        initial={{ scale: 0.9, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.9, opacity: 0 }}
        className="bg-white rounded-xl w-full max-w-lg max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="p-6 border-b bg-red-50">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-red-600" />
              <h3 className="text-lg font-bold">Report a Problem</h3>
            </div>
            <Button variant="ghost" size="sm" onClick={onClose}>
              <X className="w-4 h-4" />
            </Button>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div>
            <label className="text-sm font-medium">Problem Type *</label>
            <select
              value={formData.problem_type}
              onChange={(e) => setFormData({ ...formData, problem_type: e.target.value })}
              className="w-full h-10 border rounded-md px-3"
              required
            >
              <option value="">Select problem type...</option>
              {problemTypes.map((type) => (
                <option key={type.id} value={type.id}>{type.label}</option>
              ))}
            </select>
          </div>

          {orders.length > 0 && (
            <div>
              <label className="text-sm font-medium">Related Order (optional)</label>
              <select
                value={formData.order_id}
                onChange={(e) => setFormData({ ...formData, order_id: e.target.value })}
                className="w-full h-10 border rounded-md px-3"
              >
                <option value="">None</option>
                {orders.map((order) => (
                  <option key={order.id} value={order.id}>{order.order_number}</option>
                ))}
              </select>
            </div>
          )}

          <div>
            <label className="text-sm font-medium">Subject *</label>
            <Input
              value={formData.subject}
              onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
              placeholder="Brief description of the issue"
              required
            />
          </div>

          <div>
            <label className="text-sm font-medium">Description *</label>
            <Textarea
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              placeholder="Please provide details about the problem..."
              rows={4}
              required
            />
          </div>

          <div>
            <label className="text-sm font-medium">Contact Phone</label>
            <Input
              value={formData.contact_phone}
              onChange={(e) => setFormData({ ...formData, contact_phone: e.target.value })}
              placeholder="+386..."
            />
          </div>

          <div className="flex gap-3 pt-4">
            <Button type="button" variant="outline" onClick={onClose} className="flex-1">Cancel</Button>
            <Button type="submit" disabled={submitting} variant="destructive" className="flex-1" data-testid="submit-problem-btn">
              {submitting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Send className="w-4 h-4 mr-2" />}
              Submit Report
            </Button>
          </div>
        </form>
      </motion.div>
    </motion.div>
  );
}

// FCL Tracking Modal (for non-logged in users)
function FCLTrackingModal({ onClose }) {
  const [containerNumber, setContainerNumber] = useState("");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  const handleTrack = async () => {
    if (!containerNumber.trim()) {
      toast.error("Please enter a container number");
      return;
    }

    setLoading(true);
    try {
      const response = await axios.get(`${API_URL}/wms/customer/fcl-tracking?container_number=${encodeURIComponent(containerNumber)}`);
      setResult(response.data);
    } catch (error) {
      toast.error("Failed to track container");
    } finally {
      setLoading(false);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
      onClick={onClose}
    >
      <motion.div
        initial={{ scale: 0.9, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.9, opacity: 0 }}
        className="bg-white rounded-xl w-full max-w-lg"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="p-6 border-b">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Ship className="w-5 h-5 text-blue-600" />
              <h3 className="text-lg font-bold">FCL Tracking - Port of Koper</h3>
            </div>
            <Button variant="ghost" size="sm" onClick={onClose}>
              <X className="w-4 h-4" />
            </Button>
          </div>
        </div>

        <div className="p-6">
          <div className="flex gap-3 mb-6">
            <Input
              value={containerNumber}
              onChange={(e) => setContainerNumber(e.target.value.toUpperCase())}
              onKeyDown={(e) => e.key === "Enter" && handleTrack()}
              placeholder="Enter container number"
            />
            <Button onClick={handleTrack} disabled={loading}>
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
            </Button>
          </div>

          {result && (
            <div className="border rounded-lg p-4">
              <div className="flex items-center justify-between mb-4">
                <h4 className="font-bold">{result.container_number}</h4>
                <span className={`px-2 py-1 rounded text-xs ${
                  result.found_in_system ? "bg-green-100 text-green-700" : "bg-yellow-100 text-yellow-700"
                }`}>
                  {result.found_in_system ? result.status : "Not found"}
                </span>
              </div>
              {result.found_in_system ? (
                <div className="text-sm space-y-2">
                  {result.order_number && <p>Order: <span className="font-medium text-blue-600">{result.order_number}</span></p>}
                  {result.vessel_name && <p>Vessel: <span className="font-medium">{result.vessel_name}</span></p>}
                  {result.eta && <p>ETA: <span className="font-medium">{new Date(result.eta).toLocaleDateString()}</span></p>}
                </div>
              ) : (
                <p className="text-sm text-slate-500">Container not found in our system. Please contact us for more information.</p>
              )}
            </div>
          )}
        </div>
      </motion.div>
    </motion.div>
  );
}
